import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Briefcase,
  MapPin,
  Calendar,
  CheckCircle2,
  ArrowRight,
  Send,
  X,
  FileText,
  User,
  Mail,
  Phone,
  Sparkles,
  Clock,
  DollarSign,
  Globe2,
} from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import { JOB_OPENINGS } from "../../Data/careersData";

import "../../Style/resources/career.css";

export default function Career() {
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    portfolio: "",
    experience: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setSubmitted(false);
  };

  const handleFormChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSelectedJob(null);
      setSubmitted(false);
      setFormData({
        name: "",
        email: "",
        phone: "",
        portfolio: "",
        experience: "",
        message: "",
      });
    }, 2000);
  };

  return (
    <div className="career-page">
      <Seo
        title="Careers & Job Openings | Conectify Tech"
        description="Join Conectify Tech. Explore open roles including Lead Generation Specialist with flexible hours, tiered performance payouts, and enterprise data tools."
      />

      {/* Top Banner */}
      <section className="career-banner">
        <Container>
          <div className="career-banner__content">
            <span className="career-banner__tag">
              <Briefcase size={14} /> Careers at Conectify Tech
            </span>
            <h1 className="career-banner__title">Join Our Growth Team</h1>
            <p className="career-banner__subtitle">
              We empower honest, hardworking specialists with flexible hours, tiered performance payouts, and enterprise-grade data tools.
            </p>
          </div>
        </Container>
      </section>

      {/* Main Openings Section */}
      <section className="career-body">
        <Container>
          {/* Centered & Balanced Intro */}
          <div className="career-intro">
            <div className="career-intro__tag">
              <Sparkles size={14} className="text-primary" />
              <span>Current Openings</span>
            </div>

            <h2 className="career-intro__title">
              Shape the Future of <span className="text-gradient">B2B Intelligence</span>
            </h2>

            <p className="career-intro__desc">
              Looking for driven professionals ready to deliver real pipeline results. Review the active role below and submit your profile.
            </p>

            {/* Quick Benefits Bar */}
            <div className="career-intro__perk-strip">
              <div className="career-perk-pill">
                <Clock size={14} className="text-primary" />
                <span>Flexible & Outcome-Driven</span>
              </div>
              <div className="career-perk-pill">
                <DollarSign size={14} className="text-primary" />
                <span>Target + Closure Bonus Payouts</span>
              </div>
              <div className="career-perk-pill">
                <Globe2 size={14} className="text-primary" />
                <span>Global Enterprise Accounts</span>
              </div>
            </div>
          </div>

          {/* Job Openings List */}
          <div className="career-list">
            {JOB_OPENINGS.map((job) => (
              <motion.article
                key={job.id}
                className="career-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
              >
                {/* Header Strip */}
                <div className="career-card__top">
                  <div className="career-card__title-grp">
                    <span className="career-pill career-pill--dept">{job.department}</span>
                    <h3>{job.title}</h3>
                  </div>

                  <div className="career-card__meta">
                    <span>
                      <MapPin size={14} /> {job.location}
                    </span>
                    <span>
                      <Calendar size={14} /> Posted {job.postedDate}
                    </span>
                  </div>
                </div>

                <p className="career-card__summary">{job.summary}</p>

                {/* Highlights HUD Grid */}
                <div className="career-card__highlights">
                  {job.highlights.map((hl) => {
                    const Icon = hl.icon;
                    return (
                      <div key={hl.label} className="career-hl-box">
                        <Icon size={18} className="career-hl-icon" />
                        <div>
                          <div className="career-hl-val">{hl.value}</div>
                          <div className="career-hl-lbl">{hl.label}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Job Details Section */}
                <div className="career-details-grid">
                  <div className="career-col">
                    <h4>Job Responsibilities</h4>
                    <ul className="career-list-checks">
                      {job.responsibilities.map((r, i) => (
                        <li key={i}>
                          <CheckCircle2 size={16} className="check-icon" />
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="career-col">
                    <h4>Desired Candidate Profile</h4>
                    <ul className="career-list-checks">
                      {job.qualifications.map((q, i) => (
                        <li key={i}>
                          <CheckCircle2 size={16} className="check-icon" />
                          <span>{q}</span>
                        </li>
                      ))}
                    </ul>

                    <h4 style={{ marginTop: "24px" }}>Perks & Benefits</h4>
                    <ul className="career-list-checks">
                      {job.perks.map((p, i) => (
                        <li key={i}>
                          <CheckCircle2 size={16} className="check-icon" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Application Footer Strip */}
                <div className="career-card__footer">
                  <div className="career-card__payout">
                    <strong>Compensation Note:</strong> {job.payoutNote}
                  </div>
                  <Button
                    icon={<ArrowRight size={18} />}
                    onClick={() => handleApplyClick(job)}
                  >
                    Apply for this Role
                  </Button>
                </div>
              </motion.article>
            ))}
          </div>
        </Container>
      </section>

      {/* Quick Application Modal */}
      <AnimatePresence>
        {selectedJob && (
          <div className="application-backdrop" onClick={() => setSelectedJob(null)}>
            <motion.div
              className="application-modal"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 28, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              transition={{ duration: 0.25 }}
            >
              <div className="application-modal__header">
                <div>
                  <span className="app-subtitle">Direct Application</span>
                  <h3>{selectedJob.title}</h3>
                </div>
                <button
                  type="button"
                  className="app-close-btn"
                  onClick={() => setSelectedJob(null)}
                  aria-label="Close form"
                >
                  <X size={18} />
                </button>
              </div>

              {submitted ? (
                <div className="application-success">
                  <CheckCircle2 size={44} className="text-success" />
                  <h4>Application Received!</h4>
                  <p>
                    Thank you for applying. Our talent recruitment team will review your profile and reach out via email shortly.
                  </p>
                </div>
              ) : (
                <form className="application-form" onSubmit={handleSubmit}>
                  <div className="form-group-row">
                    <div className="form-field">
                      <label>
                        <User size={14} /> Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="form-field">
                      <label>
                        <Mail size={14} /> Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={handleFormChange}
                      />
                    </div>
                  </div>

                  <div className="form-group-row">
                    <div className="form-field">
                      <label>
                        <Phone size={14} /> Phone / WhatsApp *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="+1 (555) 000-0000"
                        value={formData.phone}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="form-field">
                      <label>
                        <Briefcase size={14} /> Years of Lead Gen Experience *
                      </label>
                      <input
                        type="text"
                        name="experience"
                        required
                        placeholder="e.g. 1 year (Cold email outreach)"
                        value={formData.experience}
                        onChange={handleFormChange}
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label>
                      <FileText size={14} /> Resume Link / Portfolio URL *
                    </label>
                    <input
                      type="url"
                      name="portfolio"
                      required
                      placeholder="Google Drive, Dropbox, or LinkedIn Profile URL"
                      value={formData.portfolio}
                      onChange={handleFormChange}
                    />
                  </div>

                  <div className="form-field">
                    <label>Summary Pitch & Industry Specialization</label>
                    <textarea
                      name="message"
                      rows={3}
                      placeholder="Tell us about the industries you've generated leads for and your monthly outbound capacity..."
                      value={formData.message}
                      onChange={handleFormChange}
                    />
                  </div>

                  <div className="application-form__actions">
                    <button type="submit" className="app-submit-btn">
                      <Send size={16} /> Submit Application
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}